import { Component, OnDestroy, OnInit } from '@angular/core';
import { AppConfigService } from '../../../services/config/app-config.service';
import { TokenStorageService } from '../../../services/storage/token-storage.service';
import { environment } from '../../../../environments/environment';
import {
  DomSanitizer,
  SafeResourceUrl,
  Title,
  Meta,
} from '@angular/platform-browser';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-micro-investing-api',
  templateUrl: './micro-investing-api.component.html',
  styleUrl: './micro-investing-api.component.css',
  standalone: false,
})
export class MicroInvestingApiComponent implements OnInit, OnDestroy {
  userType: string | undefined;
  iframeUrl: SafeResourceUrl | null = null;

  private messageListener!: (event: MessageEvent) => void;
  private routeSub?: Subscription;
  private suppressNextIframeSync = false;
  private lastIframePath = '';

  private readonly iframeOrigin = (() => {
    try {
      const src =
        environment.microInvestingIframeUrl ||
        environment.microInvestingIframeBaseUrl;
      return new URL(src).origin;
    } catch {
      return window.location.origin;
    }
  })();

  constructor(
    private appConfig: AppConfigService,
    private tokenStorageService: TokenStorageService,
    private sanitizer: DomSanitizer,
    private router: Router,
    private route: ActivatedRoute,
    private title: Title,
    private meta: Meta,
  ) {}

  ngOnInit(): void {
    // Initial iframe URL (usually the base home)
    this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      environment.microInvestingIframeUrl,
    );

    /**
     * Accept posted as:
     *  - "/microinvest/docs/x/y#sec"   (child-style with reverse-proxy base)
     *  - "/docs/x/y?foo=1#sec"         (child-style without base)
     *  - "/microinvest/changelog#sec"  (child-style changelog)
     *  - "/doc/microinvest/x/y#sec"    (parent-style, from CopyHashLinks)
     *  - "https://xyz.local/microinvest/docs/..." (full URL)
     */
    const normalizePostedToAngularRelative = (raw: string) => {
      let input = raw || '';

      // If full URL, reduce to path+search+hash
      try {
        if (/^https?:\/\//i.test(input)) {
          const u = new URL(input);
          input = u.pathname + u.search + u.hash;
        }
      } catch {
        // ignore
      }

      let path = input;
      let fragment: string | undefined;

      // split hash
      const hashIdx = path.indexOf('#');
      if (hashIdx >= 0) {
        fragment = path.slice(hashIdx + 1);
        path = path.slice(0, hashIdx);
      }

      // strip query
      const qIdx = path.indexOf('?');
      if (qIdx >= 0) {
        path = path.slice(0, qIdx);
      }

      // ensure leading slash for comparisons
      if (!path.startsWith('/')) path = '/' + path;

      // parent-style posted already
      if (path.startsWith('/doc/microinvest')) {
        const rel = path.replace(/^\/doc\/microinvest\/?/, '');
        return { path: rel.replace(/\/{2,}/g, '/'), fragment };
      }

      // child-style: remove reverse-proxy base if present
      // "/microinvest/docs/..." -> "/docs/..."
      path = path.replace(/^\/microinvest\/?/, '/');

      // "/docs/..." -> "<rest>"
      if (path.startsWith('/docs/')) {
        path = path.slice(6);
      } else if (path.startsWith('/changelog')) {
        // keep "changelog..." as relative
        path = path.replace(/^\/+/, '');
        return { path: path.replace(/\/{2,}/g, '/'), fragment };
      } else if (path === '/' || path === '') {
        return { path: '', fragment };
      } else {
        // any other child routes
        path = path.replace(/^\/+/, '');
      }

      path = path.replace(/^\/+/, '');
      path = path.replace(/\/{2,}/g, '/');
      return { path, fragment };
    };

    this.messageListener = (event: MessageEvent) => {
      // Log ALL incoming postMessages (before origin check) for debugging
      console.log('[MicroInvesting postMessage] received:', {
        origin: event.origin,
        expectedOrigin: this.iframeOrigin,
        originMatch: event.origin === this.iframeOrigin,
        data: event.data,
      });

      // Security: accept only from expected iframe origin
      if (event.origin !== this.iframeOrigin) {
        console.warn('[MicroInvesting postMessage] rejected - origin mismatch');
        return;
      }

      const data: any = event.data;

      // Handle error signals from Docusaurus (404 / 403)
      if (data && typeof data === 'object' && data.type === 'GTN_ERROR') {
        const code = data.code === 403 ? 403 : 404;
        this.router.navigate([`/${code}`]);
        return;
      }

      // Expect object posted from Docusaurus:
      // { type: "GTN_NAV", path: "...", title: "...", description: "..." }
      if (data && typeof data === 'object') {
        const postedTitle: string | undefined = data.title;
        const postedDescription: string | undefined = data.description;

        if (postedTitle && postedTitle.trim()) {
          this.title.setTitle(postedTitle);
        }

        if (postedDescription && postedDescription.trim()) {
          this.meta.updateTag(
            { name: 'description', content: postedDescription },
            "name='description'",
          );
        }
      }

      const posted: string | undefined =
        typeof data === 'string' ? data : data?.path;

      if (!posted) return;

      const { path, fragment } = normalizePostedToAngularRelative(posted);

      // Guard: ignore "unchanged" - Docusaurus sends this as fallback when version
      // resolution fails; navigating to it would overwrite the correct URL
      if (path === 'unchanged' || path?.startsWith('unchanged/')) {
        return;
      }

      const segments = path
        ? ['doc', 'microinvest', ...path.split('/').filter(Boolean)]
        : ['doc', 'microinvest'];

      this.suppressNextIframeSync = true;
      this.router.navigate(segments, { replaceUrl: true, fragment });
    };

    window.addEventListener('message', this.messageListener, false);

    this.routeSub = this.route.url.subscribe(() => {
      const tree = this.router.parseUrl(this.router.url);
      const fragment = tree.fragment || '';

      // Angular route => iframe route
      // Example:
      //   /doc/microinvest/1.1.0/guides/getting-started
      //     -> /docs/1.1.0/guides/getting-started
      //   /doc/microinvest/1.1.0/guides/getting-started.md
      //     -> /docs/1.1.0/guides/getting-started.md
      const child = this.getMicroinvestChildPathFromRouterUrl(this.router.url);
      const iframePath = this.buildIframePathFromAngularChild(child);
      const finalPath = fragment ? `${iframePath}#${fragment}` : iframePath;

      console.log('[MicroInvesting routeSync] parent URL changed:', {
        routerUrl: this.router.url,
        child,
        iframePath,
        fragment,
      });

      if (this.suppressNextIframeSync) {
        this.suppressNextIframeSync = false;
        return;
      }

      if (finalPath !== this.lastIframePath) {
        this.lastIframePath = finalPath;

        // Ensure base has no trailing slash to prevent //docs
        const base = (environment.microInvestingIframeBaseUrl || '').replace(
          /\/+$/,
          '',
        );

        const url = base + finalPath;
        console.log('[MicroInvesting routeSync] setting iframe src:', url);
        this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      }
    });
  }

  private getMicroinvestChildPathFromRouterUrl(routerUrl: string): string {
    const tree = this.router.parseUrl(routerUrl);

    // Read only the primary route path. This avoids query params and hash fragments
    // being accidentally appended to the iframe URL.
    const primarySegments =
      tree.root.children['primary']?.segments.map((segment) => segment.path) ||
      [];

    const docIndex = primarySegments.findIndex(
      (segment, index) =>
        segment === 'doc' && primarySegments[index + 1] === 'microinvest',
    );

    if (docIndex === -1) {
      return '';
    }

    return primarySegments.slice(docIndex + 2).join('/');
  }

  private buildIframePathFromAngularChild(rawChild: string): string {
    const child = (rawChild || '')
      .replace(/^\/+|\/+$/g, '')
      .replace(/\/{2,}/g, '/');

    if (!child) {
      return '/';
    }

    // Keep existing changelog behavior. Changelog pages are not under /docs.
    if (child === 'changelog' || child.startsWith('changelog/')) {
      return `/${child}`.replace(/\/{2,}/g, '/');
    }

    // If a route already includes docs, do not duplicate it.
    // This keeps backward compatibility with any existing /doc/microinvest/docs/... links.
    if (child === 'docs' || child.startsWith('docs/')) {
      return `/${child}`.replace(/\/{2,}/g, '/');
    }

    // Bare version ("1.1.0") keeps a trailing slash: CloudFront treats a dotted
    // last segment as a file and would serve the SPA fallback instead of the
    // folder's index.html (the gtn-redirects entry page).
    if (/^\d+\.\d+(?:\.\d+)?$/.test(child)) {
      return `/docs/${child}/`;
    }

    // Main rule, including .md URLs:
    // /doc/microinvest/<version>/...     -> /microinvest/docs/<version>/...
    // /doc/microinvest/<version>/... .md -> /microinvest/docs/<version>/... .md
    return `/docs/${child}`.replace(/\/{2,}/g, '/');
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.messageListener, false);
    this.routeSub?.unsubscribe();
  }
}
